import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { getFirmByUrl, getRelatedFirms } from "../../data/expertiseData";

import grid1 from "../../assets/images/firm/grid1.png";
import grid2 from "../../assets/images/firm/grid2.png";

const FirmPage = () => {
  const { slug } = useParams();
  const [firm, setFirm] = useState(null);
  const [relatedFirms, setRelatedFirms] = useState([]);
  const heroRef = useRef(null);
  const aboutRef = useRef(null);
  const expertiseRef = useRef(null);
  const relatedExpertiseTitleRef = useRef(null);
  const relatedExpertiseHeadingRef = useRef(null);
  const relatedExpertiseTextRef = useRef(null);
  const relatedExpertiseButtonRef = useRef(null);
  const featuredProjectRef = useRef(null);
  const imageGridRef = useRef(null);
  const quoteRef = useRef(null);
  const newsRef = useRef(null);

  useEffect(() => {
    // Load firm data based on URL slug
    const firmData = getFirmByUrl(`/firms/${slug}`);
    if (firmData) {
      setFirm(firmData);
      // Get related firms
      const related = getRelatedFirms(firmData.id, 2);
      setRelatedFirms(related);
    }
  }, [slug]);

  useEffect(() => {
    if (!firm) return; // Don't run animations until firm data is loaded

    gsap.registerPlugin(ScrollTrigger);

    // Helper function for parallax effect
    const createParallax = (element) => {
      if (!element) return;

      gsap.to(element, {
        yPercent: 20,
        ease: "none",
        scrollTrigger: {
          trigger: element.parentElement,
          start: "top bottom",
          end: "bottom top",
          scrub: true,
        },
      });
    };

    // Apply parallax to all images
    const images = document.querySelectorAll("img:not(.rounded-full)"); // Exclude avatar/profile images
    images.forEach((img) => {
      img.style.transform = "scale(1.1)";
      createParallax(img);
    });

    // Hero animation
    if (heroRef.current) {
      const heroElements = heroRef.current.querySelectorAll("h1, p");
      const heroImage = heroRef.current.querySelector("img");
      gsap.set(heroElements, { y: 30, opacity: 0 });
      gsap.set(heroImage, { scale: 1.2, opacity: 0, y: 30 });
      const heroTimeline = gsap.timeline({ defaults: { ease: "power2.out" } });
      heroTimeline.to(heroElements, {
        y: 0,
        opacity: 1,
        duration: 0.8,
        stagger: 0.2,
      });
      heroTimeline.to(
        heroImage,
        { scale: 1.1, opacity: 1, y: 0, duration: 1.2 },
        "-=0.4"
      );
    }

    // Helper to animate all images in a section
    function animateImagesInSection(
      ref,
      direction = "y",
      stagger = 0.15,
      scaleFrom = 1.1
    ) {
      if (!ref.current) return;
      const images = ref.current.querySelectorAll("img");
      if (images.length === 0) return;
      gsap.set(images, { [direction]: 30, scale: scaleFrom, opacity: 0 });
      ScrollTrigger.create({
        trigger: ref.current,
        start: "top 85%",
        once: true,
        onEnter: () => {
          gsap.to(images, {
            [direction]: 0,
            scale: 1.1,
            opacity: 1,
            duration: 1,
            stagger,
            ease: "power2.out",
          });
        },
      });
    }

    // Animate all images in each section
    animateImagesInSection(aboutRef, "x", 0.2, 1.1);
    animateImagesInSection(expertiseRef, "x", 0.2, 1.1);
    animateImagesInSection(featuredProjectRef, "y", 0.15, 1.1);
    animateImagesInSection(imageGridRef, "y", 0.15, 1.1);
    animateImagesInSection(quoteRef, "y", 0.15, 1.1);
    animateImagesInSection(newsRef, "y", 0.15, 1.1);

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, [firm]); // Re-run animations when firm data changes

  if (!firm) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg text-gray-600">Loading firm information...</p>
      </div>
    );
  }

  const heroImage = firm.heroImage || firm.expertise[0]?.image || grid1;
  const aboutImage = firm.expertise[1]?.image || grid2;

  return (
    <div className="min-h-screen">
      <div className="my-8 md:my-16">
        {/* Hero Section */}
        <div ref={heroRef} className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-start md:items-center mb-2 md:mb-4">
            <h1 className="text-3xl md:text-[40px] leading-[1.2] font-medium">
              {firm.name}
            </h1>
          </div>
          <div className="flex justify-end items-start md:items-center mb-8 md:mb-16">
            <p className="text-[15px] w-full md:w-3/4 leading-relaxed text-gray-800 font-medium font-serif">
              {firm.overview}
            </p>
          </div>

          <div className="mb-16 md:mb-32">
            <div className="aspect-[3/2] md:aspect-[9/4] overflow-hidden">
              <img
                src={heroImage}
                alt={`${firm.name} - Featured Project`}
                className="w-full h-full object-cover bg-center bg-no-repeat"
              />
            </div>
          </div>
        </div>

        {/* About Section */}
        <div
          ref={aboutRef}
          className="flex flex-col md:grid md:grid-cols-2 gap-8 max-w-[1400px] mx-auto px-4 md:px-8 mb-16 md:mb-32"
        >
          <div className="space-y-6 md:space-y-8">
            <h2 className="text-2xl md:text-[32px] leading-[1.2] font-medium">
              About {firm.name}
            </h2>
            <div className="space-y-4 md:space-y-6 text-[15px] leading-relaxed text-gray-800 font-serif">
              <p>{firm.description}</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-800 mb-2">
                    Founded
                  </h4>
                  <p className="text-gray-600">{firm.founded}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-gray-800 mb-2">
                    Headquarters
                  </h4>
                  <p className="text-gray-600">{firm.headquarters}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="aspect-[4/3] mt-6 md:mt-0 overflow-hidden">
            <img
              src={aboutImage}
              alt={`${firm.name} - About`}
              className="w-full h-full object-cover bg-center bg-no-repeat"
            />
          </div>
        </div>

        {/* Expertise Section */}
        <div className="flex flex-col md:grid md:grid-cols-2 gap-8 max-w-[1400px] mx-auto px-4 md:px-8 mb-16 md:mb-32">
          <div className="aspect-[4/3] mt-6 md:mt-0 overflow-hidden">
            <img
              src={firm.expertise[0].image}
              alt={`${firm.name} - ${firm.expertise[0].title}`}
              className="w-full h-full object-cover bg-center bg-no-repeat"
            />
          </div>

          <div className="space-y-6 md:space-y-8">
            <h2 className="text-2xl md:text-[32px] leading-[1.2] font-medium">
              Areas of Expertise
            </h2>
            <div className="space-y-6">
              {firm.expertise.map((item, index) => (
                <div key={index} className="space-y-2">
                  <h3 className="text-lg font-medium">{item.title}</h3>
                  <p className="text-[15px] text-gray-800 font-serif">
                    {item.description}
                  </p>
                  {item.details && (
                    <p className="text-sm text-gray-600">{item.details}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* featured project section */}
        <div
          className="relative w-full bg-cover bg-center min-h-[600px] mb-16"
          style={{
            backgroundImage: `url(${firm.featuredProject?.image})`,
            backgroundPosition: "center",
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="relative z-10 w-full px-4 sm:px-8 md:px-16 lg:px-24 py-16 sm:py-20">
            <div className="ml-auto max-w-[600px] bg-white p-6 sm:p-8 md:p-12 lg:p-16 border-sky-400 border-b-[16px] sm:border-b-[24px] md:border-b-[32px]">
              <div className="flex items-center gap-4 mb-6 sm:mb-8">
                <h3
                  ref={relatedExpertiseTitleRef}
                  className="text-sky-400 text-sm sm:text-base uppercase tracking-[0.2em] font-medium"
                >
                  Featured Project
                </h3>
                <div className="h-[1px] w-20 sm:w-32 bg-sky-400"></div>
              </div>

              <h2
                ref={relatedExpertiseHeadingRef}
                className="text-gray-900 text-2xl sm:text-3xl md:text-4xl font-medium mb-4 sm:mb-6 leading-tight"
              >
                {firm.featuredProject?.title}
              </h2>

              <p
                ref={relatedExpertiseTextRef}
                className="text-gray-600 text-base sm:text-lg font-light leading-relaxed mb-6 sm:mb-8 md:mb-10 font-serif"
              >
                {firm.featuredProject?.description}
              </p>

              <Link
                to={`/firms/${firm.id}/projects/${firm.featuredProject.slug}`}
              >
                <button
                  ref={relatedExpertiseButtonRef}
                  className="inline-flex items-center text-black border border-black px-6 sm:px-8 py-2.5 sm:py-3 hover:bg-black hover:text-white transition-colors duration-300 text-sm tracking-wider font-medium"
                >
                  View Project
                </button>
              </Link>
            </div>
          </div>
        </div>

        {/* Key Projects Section */}
        <div
          ref={featuredProjectRef}
          className="max-w-[1400px] mx-auto px-4 md:px-8 mb-16 md:mb-32"
        >
          <div className="space-y-8">
            {/* Render key projects dynamically */}
            {firm.keyProjects && firm.keyProjects.length > 0 && (
              <>
                {/* First project - full width */}
                <div className="aspect-[21/9] overflow-hidden">
                  <img
                    src={firm.keyProjects[0].image}
                    alt={firm.keyProjects[0].title}
                    className="w-full h-full object-cover bg-center bg-no-repeat"
                  />
                </div>
                {/* Second and third projects - side by side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {firm.keyProjects[1] && (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={firm.keyProjects[1].image}
                        alt={firm.keyProjects[1].title}
                        className="w-full h-full object-cover bg-center bg-no-repeat"
                      />
                    </div>
                  )}
                  {firm.keyProjects[2] && (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={firm.keyProjects[2].image}
                        alt={firm.keyProjects[2].title}
                        className="w-full h-full object-cover bg-center bg-no-repeat"
                      />
                    </div>
                  )}
                </div>
                {/* Fourth project - full width */}
                {firm.keyProjects[3] && (
                  <div className="aspect-[21/9] overflow-hidden">
                    <img
                      src={firm.keyProjects[3].image}
                      alt={firm.keyProjects[3].title}
                      className="w-full h-full object-cover bg-center bg-no-repeat"
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Related Firms Section */}
        <div
          ref={newsRef}
          className="w-full px-4 md:px-16 lg:px-24 py-16 md:py-24 bg-white"
        >
          <div className="max-w-7xl mx-auto">
            <div className="mb-8 md:mb-12">
              <div className="flex items-center gap-4 mb-6 md:mb-8">
                <h3 className="text-sky-400 text-sm md:text-base uppercase tracking-[0.2em] font-medium">
                  RELATED FIRMS
                </h3>
                <div className="h-[1px] w-16 md:w-32 bg-sky-400"></div>
              </div>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-medium">
                Explore Similar Firms
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              {relatedFirms.map((relatedFirm) => (
                <Link
                  key={relatedFirm.id}
                  to={relatedFirm.url}
                  className="group block"
                >
                  <div className="aspect-[5/3] overflow-hidden mb-4 md:mb-6">
                    <img
                      src={relatedFirm.expertise[0]?.image || grid1}
                      alt={relatedFirm.name}
                      className="w-full h-full object-cover bg-center bg-no-repeat"
                    />
                  </div>
                  <div className="space-y-2 md:space-y-3">
                    <h3 className="text-lg md:text-xl font-medium group-hover:text-sky-400 transition-colors">
                      {relatedFirm.name}
                    </h3>
                    <p className="text-base md:text-lg text-gray-600">
                      {relatedFirm.description}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FirmPage;
